import { useFormContext, useWatch } from "react-hook-form";

import { type ParameterFieldParameter } from "./useParameterField";

export interface ParameterValue {
  /** What the record holds under the parameter's name; undefined when unset. */
  value: unknown;
  isSet: boolean;
  /** false when a stored value is not a string, so the control goes read-only. */
  editable: boolean;
  /** What a text control shows: the value, or the declared default while unset. */
  displayText: string;
  /** Sets the value, dirtying the form; undefined removes the key. */
  write: (next: string | undefined) => void;
}

function textOf(value: unknown, parameter: ParameterFieldParameter): string {
  if (typeof value === "string") {
    return value;
  }
  if (value !== undefined) {
    return JSON.stringify(value);
  }
  const fallback = parameter.default;
  if (fallback === undefined || fallback === null) {
    return "";
  }
  return typeof fallback === "string" ? fallback : JSON.stringify(fallback);
}

/**
 * One value in a record-by-name at form path `name`. A value nobody set is
 * absent from the record and follows the declared default when it runs;
 * `displayText` is what a text control shows meanwhile. The record is read and
 * written whole, so a name with a dot in it is never taken for a nested path.
 */
export function useParameterValue(
  name: string,
  parameter: ParameterFieldParameter,
): ParameterValue {
  const { control, setValue } = useFormContext();
  const parameters = useWatch({ control, name }) as Record<string, unknown>;
  const value = parameters[parameter.name];
  const isSet = value !== undefined;

  const write = (next: string | undefined) => {
    const { [parameter.name]: _removed, ...rest } = parameters;
    setValue(
      name,
      next === undefined ? rest : { ...rest, [parameter.name]: next },
      { shouldDirty: true },
    );
  };

  return {
    value,
    isSet,
    editable: !isSet || typeof value === "string",
    displayText: textOf(value, parameter),
    write,
  };
}
